import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "../../../../i18n/useLanguage";
import { useRemoveItemFromCart } from "../../../../queries/useRemoveItemFromCart";
import { useUpdateItemInCart } from "../../../../queries/useUpdateItemInCart";
import { useDealerPricingNotes } from "../../../../stores/hooks/useDealerPricingNotes";
import { OrderItemPackage } from "../../../../stores/slices/cartSlice";
import { Button } from "../../../../ui/Button/Button";
import { KeyValue } from "../../../../ui/KeyValue/KeyValue";
import { TrashIcon } from "../../../../ui/icons/TrashIcon";
import { PackageImages } from "../../../Packages/PackageImages/PackageImages";
import { PackageItemList } from "../../../Packages/Results/PackageItemList";
import { PriceSelect } from "../../../PriceSelect/PriceSelect";
import { LineItems } from "./LineItems/LineItems";

type OrderItemProps = {
  isOrder: boolean;
  orderItem: OrderItemPackage;
};

export const CartItemPackage: FunctionComponent<OrderItemProps> = ({
  isOrder,
  orderItem,
}) => {
  const updateItemInCart = useUpdateItemInCart();
  const { t } = useTranslation();
  const [language] = useLanguage();
  const removeItemFromCart = useRemoveItemFromCart();
  const packageNotes = useDealerPricingNotes(orderItem.itemType);

  return (
    <div
      className="border-b md:border md:border-primary pb-6 md:p-4 md:rounded-panel space-y-4"
      key={orderItem.itemId}
    >
      <div className="flex items-start space-x-4">
        <div className="flex gap-2 flex-col md:flex-row">
          <PackageImages
            className="w-14 h-14 md:w-32 md:h-32"
            part={orderItem.itemPart}
          />
        </div>
        <div className="flex-1">
          <div className="flex space-x-2 items-start">
            <h2 className="flex-1 text-lg md:text-2xl md:mb-2 font-medium">
              {orderItem.itemTitle[language] || orderItem.itemTitle["en"]}
            </h2>
            {!isOrder && (
              <Button
                onClick={(): void => {
                  removeItemFromCart.send(orderItem);
                }}
                aria-label="removeItemFromCart"
                busy={removeItemFromCart.loading}
                className="text-ink-2 font-light -mt-0.5 px-2"
                disabled={removeItemFromCart.loading}
              >
                <TrashIcon />
                <span className="hidden md:inline">{t("cart.remove")}</span>
              </Button>
            )}
          </div>
          <div className="grid sm:grid-cols-2">
            <div className="hidden sm:block space-y-1">
              <KeyValue className="items-start">
                <>{t("searchResults.partNumber")}:</>
                <>
                  {orderItem.itemPart.partDetails.primaryPart.packagePartNumber}
                </>
              </KeyValue>
              <PackageItemList
                className="text-sm"
                primaryPart={orderItem.itemPart.partDetails.primaryPart}
              />
            </div>
            <div className="space-y-1">
              <PriceSelect
                onChange={([{ pricingOption }]): void => {
                  updateItemInCart.send(orderItem, pricingOption);
                }}
                busy={updateItemInCart.loading}
                disabled={removeItemFromCart.loading || isOrder}
                isStaggered={false}
                orderItem={orderItem}
                part={orderItem.itemPart}
              />
              {packageNotes && <div className="text-sm">{packageNotes}</div>}
            </div>
          </div>
        </div>
      </div>
      <LineItems isOrder={isOrder} orderItem={orderItem} />
    </div>
  );
};
